import { useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import "../../styles/home.css";
import "../../styles/how-prufyy-works.css";
import { initScrollReveal } from "../../utils/scrollReveal";
import { initScrollProgress } from "../../utils/scrollProgress";

function Angel() {
  useEffect(() => {
    initScrollReveal();
    initScrollProgress();
  }, []);

  return (
    <>
      <Header />

      {/* SCROLL PROGRESS */}
      <div className="scroll-progress">
        <div className="scroll-progress-fill" />
      </div>

      <main className="hpw-wrapper">
        {/* ================= HERO ================= */}
        <section className="hero-section reveal">
          <div className="hero-content">
            <h1>
              Early-Stage Investing Is Not About Certainty <br />
              It Is About Honest Signal
            </h1>
            <p>
              Angel investors make decisions with limited data, personal capital,
              and incomplete visibility. The risk is not intuition — it is
              distorted or unsupported assumptions.
            </p>
          </div>
        </section>

        {/* ================= AUDIENCE ================= */}
        <section className="section reveal">
          <span className="audience-badge">Angel Investor Perspective</span>
          <h2>For Angels Making Independent Early-Stage Decisions</h2>
          <p>
            Angels invest before systems can model outcomes. Judgment,
            experience, and pattern recognition matter more than frameworks —
            but structure helps keep those instincts honest.
          </p>
        </section>

        {/* ================= PROBLEM ================= */}
        <section className="section reveal">
          <h2>Where Angel Decisions Get Distorted</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Story Over Substance</h4>
              <p>Charismatic storytelling outweighs real evidence.</p>
            </div>

            <div className="info-card">
              <h4>Recycled Proof</h4>
              <p>The same proof is reused across unrelated claims.</p>
            </div>

            <div className="info-card">
              <h4>Social Signaling</h4>
              <p>External opinions influence conviction.</p>
            </div>

            <div className="info-card">
              <h4>Fading Memory</h4>
              <p>Details blur across multiple deals.</p>
            </div>

            <div className="info-card">
              <h4>Undocumented Assumptions</h4>
              <p>Early beliefs are never written down.</p>
            </div>
          </div>
        </section>

        {/* ================= NEED ================= */}
        <section className="section reveal">
          <h2>What Angels Actually Need</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Claims vs Conviction</h4>
              <p>Clear separation between claims and belief.</p>
            </div>

            <div className="info-card">
              <h4>Targeted Evidence</h4>
              <p>Proof exactly where doubt exists.</p>
            </div>

            <div className="info-card">
              <h4>Private Evaluation</h4>
              <p>Decision-making without external influence.</p>
            </div>

            <div className="info-card">
              <h4>Decision Record</h4>
              <p>A trace of what was evaluated and why.</p>
            </div>

            <div className="info-card">
              <h4>Controlled Sharing</h4>
              <p>Share selectively with trusted peers.</p>
            </div>
          </div>
        </section>

        {/* ================= STRUCTURE ================= */}
        <section className="section reveal">
          <h2>Supporting Intuition with Structure</h2>

          <div className="flow-diagram">
            <div className="flow-step">
              <div className="flow-icon">🎤</div>
              <h3>Founder Pitch</h3>
              <p>Conviction and narrative are presented.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🧩</div>
              <h3>Claims Isolated</h3>
              <p>Assumptions are separated from belief.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">📎</div>
              <h3>Evidence Attached</h3>
              <p>Supporting proof appears in context.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🧠</div>
              <h3>Personal Evaluation</h3>
              <p>Decisions remain individual and private.</p>
            </div>
          </div>
        </section>

        {/* ================= EXAMPLE ================= */}
        <section className="section reveal">
          <h2>Example: Early-Stage Demand Under Scrutiny</h2>

          <div className="claim-evidence">
            <div className="claim-box">
              <strong>Claim</strong>
              <p>“We have validated strong user demand.”</p>
            </div>

            <div className="link-line" />

            <div className="evidence-box">
              <strong>Evidence</strong>
              <ul>
                <li>Pilot user activity</li>
                <li>Customer interviews</li>
                <li>Early retention snapshots</li>
              </ul>
            </div>
          </div>

          <p style={{ marginTop: 24 }}>
            Angels do not need certainty. They need clarity on what supports the
            claim and what remains unproven.
          </p>
        </section>

        {/* ================= PRIVACY ================= */}
        <section className="section reveal">
          <h2>Judgment Without Influence</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Private Reviews</h4>
              <p>No visibility into other evaluations.</p>
            </div>

            <div className="info-card">
              <h4>No Social Proof</h4>
              <p>No signaling loops shape decisions.</p>
            </div>

            <div className="info-card">
              <h4>Drafts Stay Private</h4>
              <p>Opinions are formed independently.</p>
            </div>

            <div className="info-card">
              <h4>Explicit Submission</h4>
              <p>Judgment is shared intentionally.</p>
            </div>
          </div>
        </section>

        {/* ================= MEMORY ================= */}
        <section className="section reveal">
          <h2>Decision Traceability Over Time</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Remember Why</h4>
              <p>Retain the reasoning behind investments.</p>
            </div>

            <div className="info-card">
              <h4>Track Claims</h4>
              <p>Know which assertions mattered.</p>
            </div>

            <div className="info-card">
              <h4>Learn Honestly</h4>
              <p>Improve without hindsight bias.</p>
            </div>

            <div className="info-card">
              <h4>Personal Memory</h4>
              <p>Build a long-term investment record.</p>
            </div>
          </div>
        </section>

        {/* ================= DISCRETION ================= */}
        <section className="section reveal">
          <h2>Trust and Discretion by Design</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>No Public Discovery</h4>
              <p>Startups are never surfaced publicly.</p>
            </div>

            <div className="info-card">
              <h4>No Endorsements</h4>
              <p>Sharing never implies validation.</p>
            </div>

            <div className="info-card">
              <h4>Revocable Access</h4>
              <p>Control who sees what, anytime.</p>
            </div>

            <div className="info-card">
              <h4>Time-Bound Sharing</h4>
              <p>Access expires automatically.</p>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section className="final-cta-section reveal">
          <h2>Evaluate Founders with Clarity</h2>
          <p>
            Prufyy helps angel investors preserve judgment while reducing noise
            and assumption-driven risk.
          </p>
          <a href="/signup" className="btn-primary">
            Get Started with Prufyy
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Angel;
